import { Command } from 'commander'
import chalk from 'chalk'
import inquirer from 'inquirer'
import path from 'path'
import fs from 'fs-extra'
import open from 'open'
import { Table } from 'console-table-printer'
import { DocumentManager, DocumentType, DocumentCategory, DocumentStatus } from '../core/document-manager.js'
import { formatDate, formatBytes } from '../utils/format.js'

export function createDocCommand(): Command {
  const doc = new Command('doc')
    .description('Document management commands')
  
  // List documents
  doc
    .command('list')
    .alias('ls')
    .description('List all documents')
    .option('-t, --type <type>', 'Filter by type')
    .option('-c, --category <category>', 'Filter by category')
    .option('-s, --status <status>', 'Filter by status')
    .option('--tag <tag>', 'Filter by tag')
    .option('--table', 'Display as table')
    .action(async (options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      
      // Build filter
      const filter: any = {}
      if (options.type) filter.type = options.type
      if (options.category) filter.category = options.category
      if (options.status) filter.status = options.status
      if (options.tag) filter.tags = [options.tag]
      
      const docs = await docManager.list(filter)
      
      if (docs.length === 0) {
        console.log(chalk.yellow('No documents found'))
        return
      }
      
      console.log(chalk.blue.bold(`\n📚 Documents (${docs.length})\n`))
      
      if (options.table) {
        // Table display
        const table = new Table({
          columns: [
            { name: 'id', title: 'ID', alignment: 'left' },
            { name: 'title', title: 'Title', alignment: 'left' },
            { name: 'type', title: 'Type', alignment: 'left' },
            { name: 'category', title: 'Category', alignment: 'left' },
            { name: 'status', title: 'Status', alignment: 'left' },
            { name: 'author', title: 'Author', alignment: 'left' },
            { name: 'updated', title: 'Updated', alignment: 'left' }
          ]
        })
        
        docs.forEach(doc => {
          table.addRow({
            id: doc.id,
            title: doc.title,
            type: doc.type,
            category: doc.category,
            status: getStatusLabel(doc.status),
            author: doc.author,
            updated: formatDate(doc.updated)
          })
        })
        
        table.printTable()
      } else {
        // Group by status
        const groups = {
          draft: docs.filter(d => d.status === 'draft'),
          review: docs.filter(d => d.status === 'review'),
          published: docs.filter(d => d.status === 'published'),
          archived: docs.filter(d => d.status === 'archived')
        }
        
        for (const [status, statusDocs] of Object.entries(groups)) {
          if (statusDocs.length === 0) continue
          
          console.log(chalk.white.bold(getStatusLabel(status as DocumentStatus) + ':'))
          statusDocs.forEach(doc => {
            console.log(
              chalk.gray('  • ') +
              chalk.cyan(doc.title) +
              chalk.gray(' [') + chalk.blue(doc.type) + chalk.gray(']') +
              chalk.gray(' - ') + chalk.green(doc.author) +
              chalk.gray(' (') + formatDate(doc.updated) + chalk.gray(')')
            )
            if (doc.tags.length > 0) {
              console.log(chalk.gray('    Tags: ') + doc.tags.map(t => chalk.yellow('#' + t)).join(' '))
            }
          })
          console.log()
        }
      }
    })
  
  // Create document
  doc
    .command('create')
    .alias('new')
    .description('Create a new document')
    .option('-t, --title <title>', 'Document title')
    .option('--type <type>', 'Document type')
    .option('--template <template>', 'Use template')
    .action(async (options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const answers = await inquirer.prompt([
        {
          type: 'input',
          name: 'title',
          message: 'Document title:',
          default: options.title,
          validate: (input) => input.trim() !== ''
        },
        {
          type: 'list',
          name: 'type',
          message: 'Document type:',
          choices: [
            { name: '📖 Guide', value: 'guide' },
            { name: '🔌 API Documentation', value: 'api' },
            { name: '📚 Tutorial', value: 'tutorial' },
            { name: '📋 Reference', value: 'reference' },
            { name: '📐 Specification', value: 'spec' }
          ],
          default: options.type || 'guide'
        },
        {
          type: 'list',
          name: 'category',
          message: 'Document category:',
          choices: [
            { name: '🛍️ Product', value: 'product' },
            { name: '🔧 Technical', value: 'tech' },
            { name: '⚙️ System', value: 'system' },
            { name: '👤 User', value: 'user' }
          ],
          default: 'tech'
        },
        {
          type: 'input',
          name: 'tags',
          message: 'Tags (comma-separated):',
          filter: (input) => input.split(',').map(t => t.trim()).filter(Boolean)
        },
        {
          type: 'confirm',
          name: 'useTemplate',
          message: 'Use template?',
          default: true
        }
      ])
      
      // Select or create content
      let content = ''
      if (answers.useTemplate) {
        content = getDocTemplate(answers.type, answers.title)
      } else {
        const { content: inputContent } = await inquirer.prompt([
          {
            type: 'editor',
            name: 'content',
            message: 'Edit document content (Markdown format):'
          }
        ])
        content = inputContent
      }
      
      const docManager = new DocumentManager(workflowDir)
      const doc = await docManager.create({
        title: answers.title,
        content,
        type: answers.type as DocumentType,
        category: answers.category as DocumentCategory,
        tags: answers.tags,
        author: process.env.USER || 'unknown'
      })
      
      console.log(chalk.green(`\n✅ Document created successfully!`))
      console.log(chalk.gray(`   Path: .workflow/docs/${doc.id}.md`))
      console.log(chalk.gray(`   ID: ${doc.id}`))
      
      // Ask if open in editor
      const { openEditor } = await inquirer.prompt([
        {
          type: 'confirm',
          name: 'openEditor',
          message: 'Open in editor?',
          default: true
        }
      ])
      
      if (openEditor) {
        const docPath = path.join(workflowDir, 'docs', `${doc.id}.md`)
        await open(docPath)
      }
    })
  
  // View document
  doc
    .command('view <docId>')
    .description('View document content')
    .option('-r, --raw', 'Show raw Markdown')
    .option('-m, --metadata', 'Show metadata only')
    .option('-o, --open', 'Open in browser')
    .action(async (docId, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      const document = await docManager.getById(docId)
      
      if (!document) {
        console.log(chalk.red(`❌ Document not found: ${docId}`))
        return
      }
      
      if (options.metadata) {
        console.log(chalk.blue.bold('\n📋 Document Metadata\n'))
        const metadata = {
          id: document.id,
          title: document.title,
          type: document.type,
          category: document.category,
          author: document.author,
          created: document.created,
          updated: document.updated,
          tags: document.tags,
          version: document.version,
          status: document.status
        }
        console.log(JSON.stringify(metadata, null, 2))
        return
      }
      
      if (options.raw) {
        console.log(document.content)
        return
      }
      
      if (options.open) {
        // Export as HTML and open in browser
        const html = await docManager.exportAsHtml(document.id)
        const tempFile = path.join(workflowDir, 'temp', `${document.id}.html`)
        await fs.ensureDir(path.dirname(tempFile))
        await fs.writeFile(tempFile, html)
        await open(tempFile)
        return
      }
      
      // Terminal render
      console.log(chalk.blue.bold(`\n📄 ${document.title}\n`))
      console.log(chalk.gray(`Author: ${document.author} | Updated: ${formatDate(document.updated)}`))
      console.log(chalk.gray(`Type: ${document.type} | Category: ${document.category} | Status: ${document.status}`))
      if (document.tags.length > 0) {
        console.log(chalk.gray('Tags: ') + document.tags.map(t => chalk.yellow('#' + t)).join(' '))
      }
      console.log(chalk.gray('─'.repeat(80)))
      console.log()
      console.log(renderMarkdown(document.content))
    })
  
  // Search documents
  doc
    .command('search <query>')
    .description('Search documents')
    .option('-t, --title', 'Search titles only')
    .option('-c, --content', 'Search content only')
    .option('-g, --tag', 'Search tags only')
    .action(async (query, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      const results = await docManager.search(query)
      
      if (results.length === 0) {
        console.log(chalk.yellow(`No documents found matching "${query}"`))
        return
      }
      
      console.log(chalk.blue.bold(`\n🔍 Search Results (${results.length})\n`))
      
      results.forEach(({ document, score, highlights }) => {
        console.log(chalk.cyan.bold(document.title))
        console.log(chalk.gray(`  ID: ${document.id} | Type: ${document.type} | Updated: ${formatDate(document.updated)}`))
        console.log(chalk.gray(`  Match score: ${(score * 100).toFixed(1)}%`))
        
        if (highlights && highlights.length > 0) {
          console.log(chalk.gray('  Matches:'))
          highlights.forEach(highlight => {
            console.log(chalk.gray('    ') + highlightQuery(highlight, query))
          })
        }
        
        console.log()
      })
    })
  
  // Export document
  doc
    .command('export <docId>')
    .description('Export document')
    .option('-f, --format <format>', 'Export format (md|html)', 'md')
    .option('-o, --output <path>', 'Output path')
    .action(async (docId, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      const document = await docManager.getById(docId)
      
      if (!document) {
        console.log(chalk.red(`❌ Document not found: ${docId}`))
        return
      }
      
      let outputPath = options.output
      if (!outputPath) {
        outputPath = `./${document.title.replace(/[^a-zA-Z0-9]/g, '_')}.${options.format}`
      }
      
      switch (options.format) {
        case 'md':
          const markdown = await docManager.exportAsMarkdown(document.id)
          await fs.writeFile(outputPath, markdown)
          break
          
        case 'html':
          const html = await docManager.exportAsHtml(document.id)
          await fs.writeFile(outputPath, html)
          break
          
        default:
          console.log(chalk.red(`Unsupported format: ${options.format}`))
          return
      }
      
      const stat = await fs.stat(outputPath)
      console.log(chalk.green(`✅ Document exported successfully!`))
      console.log(chalk.gray(`   Path: ${outputPath}`))
      console.log(chalk.gray(`   Size: ${formatBytes(stat.size)}`))
    })
  
  // Update document status
  doc
    .command('status <docId> <status>')
    .description('Update document status')
    .action(async (docId, status) => {
      const validStatuses = ['draft', 'review', 'published', 'archived']
      
      if (!validStatuses.includes(status)) {
        console.log(chalk.red(`❌ Invalid status: ${status}`))
        console.log(chalk.gray(`   Valid statuses: ${validStatuses.join(', ')}`))
        return
      }
      
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      const updated = await docManager.update(docId, { status: status as DocumentStatus })
      
      if (!updated) {
        console.log(chalk.red(`❌ Document not found: ${docId}`))
        return
      }
      
      console.log(chalk.green(`✅ Document status updated to: ${getStatusLabel(status as DocumentStatus)}`))
    })
  
  // Delete document
  doc
    .command('delete <docId>')
    .alias('rm')
    .description('Delete a document')
    .option('-f, --force', 'Skip confirmation')
    .action(async (docId, options) => {
      const workflowDir = path.join(process.cwd(), '.workflow')
      
      if (!await fs.pathExists(workflowDir)) {
        console.log(chalk.red('❌ Current directory is not a workflow project'))
        return
      }
      
      const docManager = new DocumentManager(workflowDir)
      const document = await docManager.getById(docId)
      
      if (!document) {
        console.log(chalk.red(`❌ Document not found: ${docId}`))
        return
      }
      
      if (!options.force) {
        const { confirm } = await inquirer.prompt([
          {
            type: 'confirm',
            name: 'confirm',
            message: `Delete document "${document.title}"?`,
            default: false
          }
        ])
        
        if (!confirm) {
          console.log(chalk.yellow('Deletion cancelled'))
          return
        }
      }
      
      const deleted = await docManager.delete(docId)
      
      if (deleted) {
        console.log(chalk.green(`✅ Document deleted: ${document.title}`))
      } else {
        console.log(chalk.red('❌ Failed to delete document'))
      }
    })
  
  return doc
}

// Helper functions
function getStatusLabel(status: DocumentStatus): string {
  const labels = {
    draft: '📝 Draft',
    review: '👀 Review',
    published: '✅ Published',
    archived: '📦 Archived'
  }
  return labels[status] || status
}

function getStatusIcon(status: DocumentStatus): string {
  const icons = {
    draft: '📝',
    review: '👀',
    published: '✅',
    archived: '📦'
  }
  return icons[status] || '❓'
}

function getDocTemplate(type: DocumentType, title: string): string {
  const templates: Record<DocumentType, string> = {
    guide: `# ${title}

## Overview

[Describe the purpose and scope of this guide]

## Background

[Provide necessary context information]

## Steps

### 1. First Step

[Detailed instructions]

### 2. Second Step

[Detailed instructions]

## Important Notes

- Note 1
- Note 2

## Related Resources

- [Resource 1]
- [Resource 2]
`,
    api: `# ${title}

## Overview

[API introduction]

## Base Information

- **Base URL**: \`https://api.example.com/v1\`
- **Authentication**: Bearer Token
- **Response Format**: JSON

## Endpoints

### 1. List Resources

\`\`\`http
GET /resources
\`\`\`

**Parameters**:
| Parameter | Type | Required | Description |
|-----------|------|----------|-------------|
| page | integer | No | Page number |
| limit | integer | No | Items per page |

**Response Example**:
\`\`\`json
{
  "data": [],
  "total": 0,
  "page": 1,
  "limit": 10
}
\`\`\`

## Error Codes

| Code | Description |
|------|-------------|
| 400 | Bad Request |
| 401 | Unauthorized |
| 404 | Not Found |
| 500 | Server Error |
`,
    tutorial: `# ${title}

## Learning Objectives

After completing this tutorial, you will be able to:
- Objective 1
- Objective 2
- Objective 3

## Prerequisites

- Prerequisite 1
- Prerequisite 2

## Tutorial Content

### Part 1: Basic Concepts

[Introduce fundamental concepts]

### Part 2: Hands-on Practice

[Step-by-step instructions]

### Part 3: Advanced Techniques

[Advanced content]

## Exercises

1. Exercise 1
2. Exercise 2

## Summary

[Key takeaways]

## Next Steps

- Recommended reading
- Related tutorials
`,
    reference: `# ${title}

## Introduction

[Reference document introduction]

## Glossary

### Term 1
Definition and explanation

### Term 2
Definition and explanation

## Detailed Information

### Topic 1

[Detailed content]

### Topic 2

[Detailed content]

## Examples

### Example 1
\`\`\`
[Example code or configuration]
\`\`\`

## Related Links

- [Link 1]
- [Link 2]
`,
    spec: `# ${title}

## 1. Introduction

### 1.1 Purpose
[Explain the purpose of this specification]

### 1.2 Scope
[Define the scope of this specification]

### 1.3 Definitions
- **Term 1**: Definition
- **Term 2**: Definition

## 2. Requirements

### 2.1 General Requirements
[List general requirements]

### 2.2 Specific Requirements

#### 2.2.1 Requirement 1
- Detail 1
- Detail 2

#### 2.2.2 Requirement 2
- Detail 1
- Detail 2

## 3. Implementation Guidelines

[Implementation recommendations]

## 4. Validation Methods

[How to verify compliance]

## 5. References

- [Reference 1]
- [Reference 2]
`
  }
  
  return templates[type] || templates.guide
}

function renderMarkdown(content: string): string {
  // Simple Markdown rendering for terminal
  return content
    .replace(/^# (.+)$/gm, chalk.bold.blue('$1'))
    .replace(/^## (.+)$/gm, chalk.bold.cyan('$1'))
    .replace(/^### (.+)$/gm, chalk.bold.green('$1'))
    .replace(/`([^`]+)`/g, chalk.gray('$1'))
    .replace(/\*\*([^*]+)\*\*/g, chalk.bold('$1'))
    .replace(/\*([^*]+)\*/g, chalk.italic('$1'))
    .replace(/^- (.+)$/gm, chalk.gray('  • ') + '$1')
    .replace(/^\d+\. (.+)$/gm, (match, p1, offset, string) => {
      const num = match.match(/^(\d+)/)?.[1] || '1'
      return chalk.gray(`  ${num}. `) + p1
    })
}

function highlightQuery(text: string, query: string): string {
  const regex = new RegExp(`(${query})`, 'gi')
  return text.replace(regex, chalk.yellow.bold('$1'))
}